import { Link } from "react-router-dom";
import { Compass, Target, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";

const IMG_ECOSYSTEM =
  "https://static.prod-images.emergentagent.com/jobs/946b3673-7a0e-484a-808e-d45d9c8ae2d9/images/3e5af7d85ef50d0aa038a50eb8d8284172adb253bc7ff75ff237ae4cd7773085.jpeg";

const VALUES = [
  {
    title: "Integrity First",
    desc: "Advice that survives scrutiny — we recommend what fits you, not what pays us faster.",
  },
  {
    title: "Structural Precision",
    desc: "Capital, code or construction — every engagement is engineered, documented and tracked.",
  },
  {
    title: "Kinetic Innovation",
    desc: "We move with the market: new instruments, new channels, new tools, adopted early.",
  },
  {
    title: "Long-Term Partnership",
    desc: "Our favourite engagements are the ones that have run for years, not weeks.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Evolve Elite — Ahmedabad, Gujarat"
        title={["One consultancy.", "Every dimension of growth."]}
        sub="Evolve Elite operates as a multi-disciplinary growth partner, simplifying complex business and personal milestones for clients across Gujarat and beyond. Headquartered in Ahmedabad and led by Viraj Vadher, our consultancy is built on ten core pillars: Loans & Funding, Wealth Portfolios, Insurance Solutions, Credit Card Liquidity, Digital Marketing, IT Services, Interior Architecture, Videography & Photography, Event Management, and Destination Weddings."
      >
        <Link
          to="/contact"
          className="btn-primary"
          data-testid="about-contact-link"
        >
          Start a Conversation
        </Link>
      </PageHero>

      {/* VISION & MISSION */}
      <section
        className="py-16 sm:py-24 lg:py-32"
        data-testid="vision-mission-section"
      >
        <div className="container-x grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Reveal>
            <div
              className="grain flex h-full flex-col rounded-3xl bg-navy p-8 sm:p-10"
              data-testid="vision-card"
            >
              <Compass className="h-9 w-9 text-gold" />
              <h2 className="mt-6 font-display text-2xl font-extrabold text-white sm:text-3xl">
                Our Vision
              </h2>
              <p className="mt-4 text-base leading-relaxed text-white/70">
                To be Gujarat's most trusted single-window consultancy — where
                capital, technology and space converge into complete ecosystems
                for sustainable growth.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div
              className="flex h-full flex-col rounded-3xl border border-gold/40 bg-gold/10 p-8 sm:p-10"
              data-testid="mission-card"
            >
              <Target className="h-9 w-9 text-amber-600" />
              <h2 className="mt-6 font-display text-2xl font-extrabold text-navy sm:text-3xl">
                Our Mission
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-700">
                To take the friction out of growth by providing transparent, multi-disciplinary advisory that equips
                our clients with the liquidity to scale, the digital tools to dominate their market, and the physical
                environments to thrive — all managed seamlessly by one dedicated team.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ECOSYSTEM VS TRANSACTIONS */}
      <section
        className="border-y border-slate-200 bg-[#F8FAFC] py-16 sm:py-24 lg:py-32"
        data-testid="ecosystem-section"
      >
        <div className="container-x grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow">Our Philosophy</p>
              <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                One dedicated partner, not a dozen vendors.
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                Modern success requires a dual approach — securing the right financial liquidity while establishing a
                dominant operational and physical presence. Managing separate financial brokers, web developers, and
                interior designers drains your time and dilutes your strategy. Evolve Elite solves this by acting as
                your single, dedicated partner.
              </p>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                We structure the capital you need through strategic commercial and retail loans, protect your assets
                with comprehensive wealth and insurance portfolios, and engineer your digital dominance through custom
                web development, SEO, and targeted Google marketing. Simultaneously, our architectural division
                transforms the physical environments where you live and work. By consolidating these high-level
                services, Evolve Elite allows you to focus entirely on what matters most: long-term, sustainable
                success.
              </p>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                "Your loan desk knows what your marketing plan needs",
                "Your interior handover is timed to your funding calendar",
                "Your CRM captures what your campaigns promise",
                "One review covers wealth, growth and workspace",
              ].map((point, i) => (
                <Reveal key={point} delay={0.08 * i}>
                  <div className="flex h-full items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                    <p className="text-sm font-bold text-navy">{point}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div
              className="relative h-full min-h-[360px]"
              data-testid="ecosystem-image-frame"
            >
              <img
                src={IMG_ECOSYSTEM}
                alt="Evolve Elite advisory desk in a modern boardroom"
                className="absolute inset-0 h-full w-full rounded-3xl object-cover"
                data-testid="ecosystem-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section
        className="border-t border-slate-200 bg-[#F8FAFC] py-16 sm:py-24"
        data-testid="values-section"
      >
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">How we work</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Four values, no exceptions.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="card-lift h-full rounded-3xl border border-slate-200 bg-white p-7">
                  <span className="font-mono text-xs font-semibold text-amber-600">
                    0{i + 1}
                  </span>
                  <h3 className="mt-4 font-display text-xl font-extrabold text-navy">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {v.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
